// frontend/src/pages/SetupCheckoutPage.jsx
import React, { useState } from 'react';
import { Box, Button, Flex, H1, H4, InlineMessage, Panel, Text } from '@bigcommerce/big-design';
import { useNavigate } from 'react-router-dom';
import { getStoreHash } from '../api/config';

const SetupCheckoutPage = () => {
  const [pendingMode, setPendingMode] = useState(null); // 'native' | 'custom' | null
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();
  const storeHash = getStoreHash();

  const chooseNativeMode = async () => {
    setPendingMode('native');
    setErrorMsg('');

    try {
      const res = await fetch('/api/checkout/mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_hash: storeHash, mode: 'native' }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || 'Unknown error');

      setSuccess(true);
      setTimeout(() => navigate('/fees'), 1500);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to enable Native Checkout.');
    } finally {
      setPendingMode(null);
    }
  };

  const chooseCustomMode = async () => {
    setPendingMode('custom');
    setErrorMsg('');

    try {
      const res = await fetch(`/api/checkout/install?store_hash=${storeHash}`, { method: 'POST' });
      const data = await res.json();

      if (!res.ok) throw new Error(data?.error || 'Unknown error');

      setSuccess(true);
      setTimeout(() => navigate('/fees'), 1500);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || 'Failed to install Custom Checkout.');
    } finally {
      setPendingMode(null);
    }
  };

  return (
    <Box margin="xxLarge" padding="large">
      <H1 style={{ marginTop: '30px' }}>Complete Your Setup</H1>

      <Text color="secondary60" marginBottom="large">
        Choose how this app delivers fees to your checkout. You can change this later from Settings.
      </Text>

      {errorMsg && (
        <InlineMessage type="error" messages={[{ text: errorMsg }]} marginBottom="medium" />
      )}
      {success && (
        <InlineMessage type="success" messages={[{ text: 'Setup complete! Redirecting to fee management...' }]} marginBottom="medium" />
      )}

      <Flex flexDirection={{ mobile: 'column', tablet: 'row' }} flexGap="1rem">
        <Panel header="Native Checkout (Recommended)" marginBottom="medium">
          <Text marginBottom="medium">
            Fees are delivered straight to BigCommerce's own checkout using its native fees API,
            kept in sync automatically whenever the cart changes. Your store keeps using
            BigCommerce's standard, auto-updating checkout, with no custom checkout script involved.
          </Text>
          <Button
            variant="primary"
            onClick={chooseNativeMode}
            isLoading={pendingMode === 'native'}
            disabled={pendingMode !== null}
          >
            {pendingMode === 'native' ? 'Setting up...' : 'Use Native Checkout'}
          </Button>
        </Panel>

        <Panel header="Custom Checkout (Advanced)" marginBottom="medium">
          <Text marginBottom="medium">
            Installs a fully custom checkout page hosted by this app. Choose this only if you need
            deeper checkout customization beyond fees; it requires more upkeep and receives
            BigCommerce checkout updates less automatically.
          </Text>
          <Button
            variant="secondary"
            onClick={chooseCustomMode}
            isLoading={pendingMode === 'custom'}
            disabled={pendingMode !== null}
          >
            {pendingMode === 'custom' ? 'Setting up...' : 'Use Custom Checkout'}
          </Button>
        </Panel>
      </Flex>

      <Box marginTop="medium">
        <H4>What happens to my order confirmations and emails?</H4>
        <Text color="secondary60">
          Both options record fees as real checkout line items, so they appear identically in order
          history and confirmation emails.
        </Text>
      </Box>
    </Box>
  );
};

export default SetupCheckoutPage;
